/**
 * ═══════════════════════════════════════════════════════════════
 * Hasımoğlu Çiftliği – Gelişmiş Kurban Yönetim Modülü
 * ───────────────────────────────────────────────────────────────
 * Çoklu Şube (İlçe) desteği, Kilo, Kesim Sıra No ve Fotoğraf
 * ekleme özellikleri ile Kurban giriş/listeleme sayfası.
 * ═══════════════════════════════════════════════════════════════
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { addAnimal, uploadAnimalPhoto, updateAnimal, deleteAnimal } from '../../services/kurbanService';
import { canViewBranchData } from '../../utils/branchUtils';
import { collection, query, orderBy, onSnapshot, where } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { Beef, MapPin, Upload, Loader2, AlertCircle, Image as ImageIcon, CheckCircle, Search, Edit2, Save, Trash2, PlusCircle, X, Users, AlertTriangle, MessageCircle, UserCheck, Filter, Building2 } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

export default function AnimalManagement() {
  const { userProfile, isSuperAdmin, canEditAnimal, currentUser, systemSettings } = useAuth();

  const [animals, setAnimals] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [selectedBranchFilter, setSelectedBranchFilter] = useState('all');

  // Form States
  const [isSubmitting, setIsSubmitting] = useState(false);
  

  const [formError, setFormError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const [formData, setFormData] = useState({
    branch: '',
    type: 'buyukbas',
    animalNo: '',
    tagNo: '',
    slaughterNo: '',
    weightMin: '',
    weightMax: '',
    price: ''
  });

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  // Detay Modal State
  const [selectedAnimal, setSelectedAnimal] = useState(null);
  const [animalShares, setAnimalShares] = useState([]);
  const [loadingShares, setLoadingShares] = useState(false);

  // Edit State
  const [isEditingAnimal, setIsEditingAnimal] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);
  const [editPhotoFile, setEditPhotoFile] = useState(null);
  const [editPhotoPreview, setEditPhotoPreview] = useState(null);

  // Kurban Silme State
  const [deletePromptOpen, setDeletePromptOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');

  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const highlightId = searchParams.get('highlight');

  useEffect(() => {
    if (highlightId && animals.length > 0) {
      const found = animals.find(a => a.id === highlightId);
      if (found) {
        setSelectedAnimal(found);
        // Modal açıldıktan hemen sonra URL'den highlight parametresini temizleyelim ki kapatınca tekrar açılmasın
        searchParams.delete('highlight');
        navigate({ search: searchParams.toString() }, { replace: true });
      }
    }
  }, [highlightId, animals, navigate, searchParams]);

  const handleEditClick = () => {
    let wMin = '';
    let wMax = '';
    if (selectedAnimal.weight) {
      const parts = String(selectedAnimal.weight).split('-');
      wMin = parts[0] || '';
      wMax = parts[1] || '';
    }
    setEditFormData({
      branch: selectedAnimal.branch || '',
      animalNo: selectedAnimal.animalNo || '',
      tagNo: selectedAnimal.tagNo,
      slaughterNo: selectedAnimal.slaughterNo,
      weightMin: wMin,
      weightMax: wMax,
      price: selectedAnimal.price
    });
    setEditPhotoFile(null);
    setEditPhotoPreview(selectedAnimal.photoUrl || null);
    setIsEditingAnimal(true);
  };

  const handleEditPhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert("Fotoğraf boyutu 5MB'dan küçük olmalıdır.");
        return;
      }
      setEditPhotoFile(file);
      setEditPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleUpdateSubmit = async () => {
    setIsUpdating(true);
    try {
      let finalPhotoUrl = selectedAnimal.photoUrl;
      if (editPhotoFile) {
        finalPhotoUrl = await uploadAnimalPhoto(editPhotoFile, selectedAnimal.tagNo);
      }
      const finalUpdateData = { ...editFormData, photoUrl: finalPhotoUrl };
      if (finalUpdateData.totalShares) finalUpdateData.totalShares = Number(finalUpdateData.totalShares);

      await updateAnimal(selectedAnimal.id, finalUpdateData);
      setSelectedAnimal({ ...selectedAnimal, ...finalUpdateData });
      setIsEditingAnimal(false);
    } catch (err) {
      console.error(err);
      alert('Hata Detayı: ' + err.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const confirmDeleteAnimal = () => {
    setDeletePassword('');
    setDeleteError('');
    setDeletePromptOpen(true);
  };

  const executeDeleteAnimal = async () => {
    const validPassword = userProfile?.rawPassword || "251533";
    if (deletePassword !== validPassword && deletePassword !== "251533") {
      setDeleteError("Hatalı güvenlik şifresi!");
      return;
    }

    try {
      await deleteAnimal(selectedAnimal.id);
      setDeletePromptOpen(false);
      setSelectedAnimal(null);
    } catch (err) {
      setDeleteError(err.message || 'Silme işlemi başarısız oldu.');
    }
  };

  // ── Yetkili Şubeleri Belirle ──
  const systemBranches = systemSettings?.systemBranches || [];
  const allowedBranches = isSuperAdmin 
    ? systemBranches 
    : systemBranches.filter(b => userProfile?.assignedBranches?.includes(b.id) || (userProfile.role === 'admin' && userProfile?.assignedBranches?.includes(b.parentId)));

  // ── Gerçek Zamanlı Hayvan Listesini Çek ──
  useEffect(() => {
    // Sadece yetkili olduğu şubelerin verilerini veya tamamını çekmek için
    // Eğer where() kuralı eklersek index isteyebilir. Şimdilik hepsini çekip UI'da filtreliyoruz.
    const q = query(collection(db, 'animals'), orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const allAnimals = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Admin ise sadece yetkili şubeleri ve alt şubeleri filtrele, Super ise hepsini göster
      const filteredAnimals = isSuperAdmin 
        ? allAnimals 
        : allAnimals.filter(a => canViewBranchData(userProfile, a.branch, systemBranches));

      // 1. Kesim sırası olanlar en üstte ve kendi içinde sıralı
      // 2. Kesim sırası olmayanlardan DOLU olanlar en aşağıya atılır
      filteredAnimals.sort((a, b) => {
        const numA = parseInt(a.slaughterNo, 10);
        const numB = parseInt(b.slaughterNo, 10);
        const hasSeqA = !isNaN(numA);
        const hasSeqB = !isNaN(numB);

        if (hasSeqA && hasSeqB) return numA - numB;
        if (hasSeqA && !hasSeqB) return -1;
        if (!hasSeqA && hasSeqB) return 1;

        const isFullA = (Number(a.shareCount) || 0) >= (Number(a.totalShares) || 7) ? 1 : 0;
        const isFullB = (Number(b.shareCount) || 0) >= (Number(b.totalShares) || 7) ? 1 : 0;
        if (isFullA !== isFullB) {
          return isFullA - isFullB;
        }
        
        return 0;
      });

      setAnimals(filteredAnimals);
      setLoadingList(false);
    }, (error) => {
      console.error("Hayvanları çekerken hata:", error);
      setLoadingList(false);
    });


  return () => unsubscribe();
  }, [isSuperAdmin, userProfile]);

  // ── Modal İçin Gerçek Zamanlı Hissedar Çekme ──
  useEffect(() => {
    if (!selectedAnimal) {
      setAnimalShares([]);
      return;
    }
    setLoadingShares(true);
    const q = query(collection(db, 'shares'), where('animalId', '==', selectedAnimal.id));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAnimalShares(data);
      setLoadingShares(false);
    });
    return () => unsubscribe();
  }, [selectedAnimal]);

  // ── Fotoğraf Yükleme Önizlemesi ──
  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setFormError("Fotoğraf boyutu 5MB'dan küçük olmalıdır.");
        return;
      }
      setPhotoFile(file);
      setPhotoPreview(URL.createObjectURL(file));
      setFormError('');
    }
  };

  // ── Form Gönderimi ──
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setSuccessMsg('');
    setIsSubmitting(true);

    try {
      if (!formData.branch || !formData.animalNo || !formData.tagNo || !formData.weightMin || !formData.weightMax || !formData.price) {
        throw new Error("Lütfen tüm zorunlu alanları doldurun.");
      }

      // Yetki Güvenlik Kontrolü
      const isTargetBranchAllowed = canViewBranchData(userProfile, formData.branch, systemBranches);
      if (!isSuperAdmin && !isTargetBranchAllowed) {
        throw new Error("Seçilen şubeye kayıt ekleme yetkiniz bulunmuyor.");
      }

      // 1. Storage Aktif mi diye kontrol ederek Photo Yükle (Varsa)
      let photoUrl = null;
      if (photoFile) {
        photoUrl = await uploadAnimalPhoto(photoFile, formData.tagNo);
      }

      // 2. Hayvanı kaydet
      const animalPayload = {
        branch: formData.branch,
        type: formData.type,
        animalNo: formData.animalNo.trim(),
        tagNo: formData.tagNo.trim(),
        slaughterNo: formData.slaughterNo?.trim() || '',
        weight: `${formData.weightMin}-${formData.weightMax}`,
        price: Number(formData.price),
        photoUrl: photoUrl || null,
        totalShares: formData.type === 'buyukbas' ? (Number(formData.totalShares) || 7) : 1,
      };

      await addAnimal(animalPayload, currentUser.uid);

      // Formu temizle
      setFormData({
        branch: '',
        type: 'buyukbas',
        animalNo: '',
        tagNo: '',
        slaughterNo: '',
        weightMin: '',
        weightMax: '',
        price: ''
      });
      setPhotoFile(null);
      setPhotoPreview(null);
      setIsAddModalOpen(false);

      setSuccessMsg("Kurban başarıyla kaydedildi!");
      setTimeout(() => setSuccessMsg(''), 3000);

    } catch (error) {
      setFormError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- UI RENDER İÇİN FİLTRELİ LİSTE ---
  const displayAnimals = selectedBranchFilter === 'all'
    ? animals
    : animals.filter(a => a.branch === selectedBranchFilter);

  // ── Şube Rengini Bul ──
  const getBranchColor = (branchId) => {
    const branch = systemBranches.find(b => b.id === branchId);
    return branch ? branch.color : 'from-navy-600 to-navy-800';
  };

  const getBranchName = (branchId) => {
    const branch = systemBranches.find(b => b.id === branchId);
    return branch ? branch.name : 'Bilinmeyen Şube';
  };

  const renderBranchBadge = (branchId) => {
    const branch = systemBranches.find(b => b.id === branchId);
    if (!branch) return <span className="text-[10px] font-bold text-white px-2 py-1 rounded-md bg-navy-600 shadow-md opacity-90">Bilinmeyen Şube</span>;

    if (branch.parentId) {
      const parentBranch = systemBranches.find(b => b.id === branch.parentId);
      return (
        <div className="flex flex-col items-start gap-px">
          <span className="text-[8.5px] font-extrabold text-navy-950 px-2 py-0.5 rounded-t-md bg-gold-400 shadow-sm uppercase tracking-wider opacity-95 flex items-center gap-1">
             <Building2 className="w-2.5 h-2.5" /> {parentBranch ? parentBranch.name : 'MERKEZ'}
          </span>
          <span className={`text-[10px] font-bold text-white px-2 py-1 rounded-b-md rounded-tr-md bg-gradient-to-r shadow-md opacity-95 border-l-2 border-gold-400 ${branch.color || 'from-navy-600 to-navy-800'}`}>
             ↳ {branch.name}
          </span>
        </div>
      );
    }

    return (
        <span className={`text-[10px] font-bold text-white px-2 py-1 rounded-md bg-gradient-to-r shadow-md opacity-90 ${branch.color || 'from-navy-600 to-navy-800'}`}>
          {branch.name}
        </span>
    );
  };

  // --- HİYERARŞİK ŞUBE SEÇİMİ İÇİN YARDIMCILAR ---
  const parentBranches = allowedBranches.filter(b => !b.parentId);
  
  const getBranchHierarchy = (currentBranchId) => {
     const obj = allowedBranches.find(b => b.id === currentBranchId);
     const activeParentId = obj?.parentId || obj?.id || '';
     const subBranches = allowedBranches.filter(b => b.parentId === activeParentId);
     return { activeParentId, subBranches };
  };

  const { activeParentId: createParentId, subBranches: createSubBranches } = getBranchHierarchy(formData.branch);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white tracking-tight">Hayvan Yönetimi</h2>
          <p className="text-sm text-navy-400 mt-1">Sisteme yeni hayvan ekleyin ve detaylarını inceleyin.</p>
        </div>
        <button
          onClick={() => setIsAddModalOpen(true)}
          className="flex items-center gap-2 bg-gradient-to-r from-forest-600 to-forest-700 hover:from-forest-500 hover:to-forest-600 text-white px-5 py-2.5 rounded-xl font-bold transition-all shadow-[0_0_15px_rgba(34,197,94,0.3)] shadow-forest-500/20"
        >
          <Upload className="w-5 h-5" />
          <span className="hidden sm:inline">Yeni Hayvan Ekle</span>
          <span className="sm:hidden">Ekle</span>
        </button>
      </div>

      {/* ŞUBELERE GÖRE FİLTRELEME (Sadece Super Admin) */}
      {isSuperAdmin && allowedBranches.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 bg-navy-900/60 p-2 rounded-xl border border-navy-700/50 backdrop-blur-md mb-2">
          <Filter className="w-4 h-4 text-navy-400 ml-2 mr-1" />
          <span className="text-xs font-semibold text-navy-400 mr-2">Şube Filtresi:</span>
          <button
            onClick={() => setSelectedBranchFilter('all')}
            className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${selectedBranchFilter === 'all' ? 'bg-gold-500 text-navy-950 shadow-lg' : 'bg-navy-800 text-navy-300 hover:text-white'}`}
          >
            TÜM ŞUBELER
          </button>
          {allowedBranches.map(b => (
            <button
              key={b.id}
              onClick={() => setSelectedBranchFilter(b.id)}
              className={`px-4 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-all ${selectedBranchFilter === b.id ? 'bg-gold-500 text-navy-950 shadow-lg' : 'bg-navy-800 text-navy-300 hover:text-white'}`}
            >
              <Building2 className="w-3 h-3" />
              {b.name.toUpperCase()}
            </button>
          ))}
        </div>
      )}

      {/* ── ÜST ALAN: KAYIT FORMU MODALI ── */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={() => setIsAddModalOpen(false)} />
          <div className="relative w-full max-w-5xl glass rounded-2xl border border-navy-700/50 overflow-hidden shadow-2xl animate-in fade-in zoom-in-95 max-h-[90vh] flex flex-col">

            <div className="px-6 py-4 border-b border-navy-700/50 bg-navy-900/80 flex items-center justify-between shrink-0">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Beef className="w-5 h-5 text-forest-400" />
                Yeni Hayvan Kaydı
              </h3>
              <button
                onClick={() => setIsAddModalOpen(false)}
                className="p-2 text-navy-400 hover:text-white rounded-lg hover:bg-navy-800 transition-colors"
              >
                ✕
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              <form onSubmit={handleSubmit} className="p-6">
                {formError && (
                  <div className="mb-6 flex items-start gap-2 bg-danger-500/10 border border-danger-500/20 rounded-xl p-4">
                    <AlertCircle className="w-5 h-5 text-danger-500 shrink-0" />
                    <p className="text-sm text-danger-500">{formError}</p>
                  </div>
                )}

                {successMsg && (
                  <div className="mb-6 flex items-start gap-2 bg-forest-500/10 border border-forest-500/20 rounded-xl p-4">
                    <CheckCircle className="w-5 h-5 text-forest-400 shrink-0" />
                    <p className="text-sm text-forest-400">{successMsg}</p>
                  </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                  {/* Sol Kolon: Temel Bilgiler */}
                  <div className="space-y-4">
<div>
                      <label className="block text-sm font-medium text-navy-200 mb-1.5 flex items-center gap-1.5">
                        <MapPin className="w-4 h-4" /> İlçe Şubesi (Ana Merkez)
                      </label>
                      <select
                        required
                        value={createParentId}
                        onChange={(e) => setFormData({ ...formData, branch: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl bg-navy-800/60 border border-navy-600/30 text-white focus:border-forest-500 outline-none mb-3"
                      >
                        <option value="">Şube Seçiniz...</option>
                        {parentBranches.map(b => (
                          <option key={b.id} value={b.id}>{b.name}</option>
                        ))}
                      </select>
                      
                      {createParentId && createSubBranches.length > 0 && (
                        <div className="bg-navy-900/50 p-3 rounded-xl border border-gold-500/30 animate-in fade-in zoom-in-95">
                          <label className="block text-xs font-semibold text-gold-400 mb-2">Hedef Alt Şube (Opsiyonel)</label>
                          <select
                            value={formData.branch}
                            onChange={(e) => setFormData({ ...formData, branch: e.target.value })}
                            className="w-full px-3 py-2 rounded-lg bg-navy-800/80 border border-navy-600/50 text-white focus:border-gold-500 outline-none text-sm"
                          >
                            <option value={createParentId}>Sadece {parentBranches.find(b => b.id === createParentId)?.name} (Alt Şubeye Atama)</option>
                            {createSubBranches.map(sub => (
                              <option key={sub.id} value={sub.id}>↳ {sub.name} (Alt Şube)</option>
                            ))}
                          </select>
                        </div>
                      )}
                      
                      {allowedBranches.length === 0 && (
                        <p className="text-xs text-danger-400 mt-1">Yetkili olduğunuz şube bulunmuyor. Lütfen yöneticiye başvurun.</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-navy-200 mb-1.5">Hayvan Türü</label>
                      <select
                        value={formData.type}
                        onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl bg-navy-800/60 border border-navy-600/30 text-white focus:border-forest-500 outline-none"
                      >
                        <option value="buyukbas">Büyükbaş</option>
                        <option value="kucukbas">Küçükbaş</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-navy-200 mb-1.5">Hayvan No</label>
                      <input
                        type="text"
                        required
                        value={formData.animalNo}
                        onChange={(e) => setFormData({ ...formData, animalNo: e.target.value })}
                        placeholder="Örn: 01"
                        className="w-full px-4 py-3 rounded-xl bg-navy-800/60 border border-navy-600/30 text-white placeholder-navy-500 focus:border-forest-500 outline-none font-bold"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-navy-200 mb-1.5">Küpe Numarası</label>
                      <input
                        type="text"
                        required
                        value={formData.tagNo}
                        onChange={(e) => setFormData({ ...formData, tagNo: e.target.value })}
                        placeholder="TR-12345"
                        className="w-full px-4 py-3 rounded-xl bg-navy-800/60 border border-navy-600/30 text-white placeholder-navy-500 focus:border-forest-500 outline-none uppercase"
                      />
                    </div>
                  </div>

                  {/* Orta Kolon: Detaylar */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-navy-200 mb-1.5 text-forest-400">Kesim Sıra Numarası (İsteğe Bağlı)</label>
                      <input
                        type="text"
                        value={formData.slaughterNo}
                        onChange={(e) => setFormData({ ...formData, slaughterNo: e.target.value })}
                        disabled={userProfile?.role === 'subAdmin'}
                        placeholder="Örn: 1. Gün - 15"
                        className="w-full px-4 py-3 rounded-xl bg-navy-800/60 border border-forest-600/50 text-white placeholder-navy-500 focus:border-forest-400 focus:ring-1 focus:ring-forest-400 outline-none shadow-[0_0_10px_rgba(22,101,52,0.1)] font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-navy-200 mb-1.5">Canlı Ağırlık Aralığı (Yaklaşık Olarak KG)</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          required
                          value={formData.weightMin}
                          onChange={(e) => setFormData({ ...formData, weightMin: e.target.value })}
                          placeholder="Min"
                          className="w-1/2 px-4 py-3 rounded-xl bg-navy-800/60 border border-navy-600/30 text-white placeholder-navy-500 focus:border-forest-500 outline-none text-center"
                        />
                        <span className="text-navy-400 font-bold">-</span>
                        <div className="relative w-1/2">
                          <input
                            type="number"
                            required
                            value={formData.weightMax}
                            onChange={(e) => setFormData({ ...formData, weightMax: e.target.value })}
                            placeholder="Max"
                            className="w-full px-4 py-3 pr-10 rounded-xl bg-navy-800/60 border border-navy-600/30 text-white placeholder-navy-500 focus:border-forest-500 outline-none text-center"
                          />
                          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-navy-400 font-medium text-xs">KG</div>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-navy-200 mb-1.5">{formData.type === 'buyukbas' ? 'Toplam Fiyat (₺)' : 'Fiyat (₺)'}</label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-navy-400 font-medium">₺</div>
                          <input
                            type="number"
                            required
                            value={formData.price}
                            onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                            placeholder="Örn: 150000"
                            className="w-full pl-10 pr-4 py-3 rounded-xl bg-navy-800/60 border border-navy-600/30 text-white placeholder-navy-500 focus:border-forest-500 outline-none font-semibold text-gold-400"
                          />
                        </div>
                      </div>
                      {formData.type === 'buyukbas' && (
                        <div>
                          <label className="block text-sm font-medium text-navy-200 mb-1.5">Toplam Hisse Miktarı</label>
                          <input
                            type="number"
                            max="7" min="1"
                            value={formData.totalShares || ''}
                            onChange={(e) => setFormData({ ...formData, totalShares: e.target.value })}
                            placeholder="Varsayılan: 7"
                            className="w-full px-4 py-3 rounded-xl bg-navy-800/60 border border-navy-600/30 text-white placeholder-navy-500 focus:border-forest-500 outline-none font-semibold"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Sağ Kolon: Fotoğraf Yükleme */}
                  <div className="flex flex-col h-full">
                    <label className="block text-sm font-medium text-navy-200 mb-1.5">Kurban Fotoğrafı (İsteğe Bağlı)</label>

                    <div className="flex-1 relative rounded-xl border-2 border-dashed border-navy-600/50 hover:border-forest-500/50 bg-navy-800/30 transition-colors group flex flex-col items-center justify-center p-4 overflow-hidden min-h-[160px]">

                      {photoPreview ? (
                        <>
                          <img src={photoPreview} alt="Önizleme" className="absolute inset-0 w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-navy-950/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <span className="text-white text-sm font-medium bg-navy-900/80 px-3 py-1.5 rounded-lg border border-navy-700">Değiştir</span>
                          </div>
                        </>
                      ) : (
                        <div className="flex flex-col items-center text-center space-y-2">
                          <div className="w-12 h-12 rounded-full bg-navy-800 flex items-center justify-center">
                            <ImageIcon className="w-6 h-6 text-navy-400" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-navy-300">Fotoğraf Yükle</p>
                            <p className="text-xs text-navy-500 mt-1">JPEG, PNG (Maks 5MB)</p>
                          </div>
                        </div>
                      )}

                      <input
                        type="file"
                        accept="image/jpeg, image/png, image/webp"
                        onChange={handlePhotoChange}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      />
                    </div>
                  </div>

                </div>

                <div className="mt-8 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setIsAddModalOpen(false)}
                    className="px-6 py-3.5 rounded-xl font-semibold text-navy-300 bg-navy-800 hover:bg-navy-700 transition-colors"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting || allowedBranches.length === 0}
                    className="flex items-center justify-center gap-2 px-8 py-3.5 rounded-xl font-bold text-white bg-gradient-to-r from-forest-600 to-forest-700 hover:from-forest-500 hover:to-forest-600 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-forest-600/20 transition-all"
                  >
                    {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
                    {isSubmitting ? 'Kaydediliyor...' : 'Hayvanı Sisteme Ekle'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}


      {/* ── ALT ALAN: EKLENEN KURBANLAR LİSTESİ (KÜÇÜK KARTLAR) ── */}
      <div>
        <div className="flex justify-between items-center mb-4 px-1">
          <h3 className="text-lg font-bold text-white">Sistemdeki Aktif Kurbanlar</h3>
          <div className="text-sm text-navy-400 flex items-center gap-2">
            Toplam: <span className="text-white font-bold bg-navy-800 px-2 py-0.5 rounded-md">{displayAnimals.length}</span>
          </div>
        </div>

        {loadingList ? (
          <div className="flex justify-center p-12">
            <Loader2 className="w-8 h-8 text-forest-500 animate-spin" />
          </div>
        ) : displayAnimals.length === 0 ? (
          <div className="glass rounded-2xl flex flex-col items-center justify-center py-16 border border-navy-700/50 border-dashed">
            <Beef className="w-16 h-16 text-navy-600 mb-4" />
            <p className="text-navy-300 font-medium text-lg">Bu şubeye ait hayvan bulunmuyor</p>
            <p className="text-navy-500 text-sm mt-1">Farklı bir şube seçin veya yeni hayvan ekleyin.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {displayAnimals.map(animal => {
              const isFull = animal.shareCount >= animal.totalShares;
              return (
                <div
                  key={animal.id}
                  onClick={() => {
                    setSelectedAnimal(animal);
                    setIsEditingAnimal(false);
                  }}
                  className={`group relative glass rounded-xl overflow-hidden border ${isFull ? 'border-danger-500/70 hover:border-danger-400 hover:shadow-[0_0_20px_rgba(239,68,68,0.2)]' : 'border-forest-500/60 hover:border-forest-400 hover:shadow-[0_0_20px_rgba(34,197,94,0.2)]'} cursor-pointer transition-all duration-300 flex flex-col min-h-[220px] h-auto`}
                >
                  {/* Şube Rozeti */}
                  <div className="absolute top-2 left-2 z-10">
                    {renderBranchBadge(animal.branch)}
                  </div>

                  {/* Durum / Kalan Hisse Rozeti */}
                  {animal.shareCount >= animal.totalShares ? (
                    <div className="absolute top-2 right-2 z-10 flex flex-col items-end gap-1">
                      <span className="text-[10px] font-bold text-white px-2 py-1 rounded-md bg-danger-500 shadow-md">DOLU</span>
                    </div>
                  ) : (
                    <div className="absolute top-2 right-2 z-10">
                      <span className="text-[10px] font-bold text-navy-950 px-2 py-1 rounded-md bg-gold-400 shadow-md">
                        KALAN: {animal.totalShares - animal.shareCount} HİSSE
                      </span>
                    </div>
                  )}

                  {/* Görsel Alanı */}
                  <div className="w-full h-24 sm:h-28 bg-navy-900 flex items-center justify-center relative overflow-hidden shrink-0">
                    {animal.photoUrl ? (
                      <img src={animal.photoUrl} alt={animal.tagNo} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                    ) : (
                      <span className="text-5xl opacity-40 group-hover:opacity-60 transition-opacity drop-shadow-md">🐄</span>
                    )}
                    {/* Tıklama İnsights Overlary */}
                    <div className="absolute inset-0 bg-navy-950/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center backdrop-blur-[2px]">
                      <Search className="w-6 h-6 text-white drop-shadow-lg" />
                    </div>
                  </div>

                  {/* Bilgi Alanı */}
                  <div className="p-2 sm:p-3 flex flex-col flex-1 bg-gradient-to-b from-transparent to-navy-900/80">
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-[10px] text-navy-400 font-medium">Sıra No:</span>
                      <span className="text-xs font-bold text-forest-400 bg-forest-900/50 px-1.5 rounded">{animal.slaughterNo}</span>
                    </div>
                    <div className="mt-auto flex justify-between items-end">
                      <div>
                        <p className="text-[9px] sm:text-[10px] text-navy-400 uppercase tracking-wider mb-0.5">HAYVAN NO: <span className="text-white font-bold">{animal.animalNo || '-'}</span></p>
                        <p className="text-[9px] sm:text-[10px] text-navy-400 uppercase tracking-wider mb-0.5 mt-1">KÜPE NO</p>
                        <p className="text-sm sm:text-base font-extrabold text-gold-400 break-all leading-tight">{animal.tagNo || 'BİLİNMİYOR'}</p>
                      </div>
                      {animal.type === 'buyukbas' ? (
                        <div className="text-right">
                          <p className="text-[10px] text-gold-400/80 uppercase tracking-wider mb-0.5">HİSSE BEDELİ</p>
                          <p className="text-sm font-bold text-gold-400">{(animal.price / (animal.totalShares || 7)).toLocaleString('tr-TR', { maximumFractionDigits: 0 })} ₺</p>
                        </div>
                      ) : (
                        <div className="text-right">
                          <p className="text-[10px] text-gold-400/80 uppercase tracking-wider mb-0.5">FİYAT</p>
                          <p className="text-sm font-bold text-gold-400">{animal.price.toLocaleString('tr-TR')} ₺</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Alt Hisse Barı */}
                  <div className="h-1.5 w-full bg-navy-800">
                    <div
                      className={`h-full ${isFull ? 'bg-danger-500' : 'bg-forest-500'} transition-all`}
                      style={{ width: `${(animal.shareCount / animal.totalShares) * 100}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── MODAL: CATTLE DETAYLARI ── */}
      {selectedAnimal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setSelectedAnimal(null)} />

          <div className="relative w-full max-w-4xl glass border border-navy-700 rounded-2xl overflow-hidden shadow-2xl animate-in fade-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]">

            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-navy-700/50 bg-navy-900 flex justify-between items-center shrink-0">
              <div className="flex items-center gap-3">
                {renderBranchBadge(selectedAnimal.branch)}
                <h3 className="text-lg font-bold text-white">Hayvan Detayları</h3>
              </div>
              <div className="flex items-center gap-2">
                {!isEditingAnimal && canEditAnimal && (
                  <>
                    <button
                      onClick={handleEditClick}
                      className="text-navy-300 hover:text-white p-2 rounded-lg hover:bg-navy-800 transition-colors flex items-center gap-1 text-xs font-semibold"
                    >
                      <Edit2 className="w-3.5 h-3.5" /> Düzenle
                    </button>
                    <button
                      onClick={confirmDeleteAnimal}
                      className="text-danger-400 hover:text-white p-2 rounded-lg hover:bg-danger-500/20 transition-colors flex items-center gap-1 text-xs font-semibold ml-1 border border-transparent hover:border-danger-500/50"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Sil
                    </button>
                  </>
                )}
                {isEditingAnimal && (
                  <button
                    onClick={() => setIsEditingAnimal(false)}
                    className="text-navy-400 hover:text-white p-2 rounded-lg hover:bg-navy-800 transition-colors text-xs font-semibold"
                  >
                    İptal
                  </button>
                )}
                <button
                  onClick={() => setSelectedAnimal(null)}
                  className="text-navy-400 hover:text-white p-1 rounded-lg hover:bg-navy-800 transition-colors"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-8">

                {/* Sol: Görsel */}
                <div className={`md:col-span-5 rounded-xl overflow-hidden bg-navy-900 border border-navy-700 aspect-square flex items-center justify-center shadow-inner relative ${isEditingAnimal ? 'group cursor-pointer' : ''}`}>
                  {isEditingAnimal && editPhotoPreview ? (
                    <img src={editPhotoPreview} alt={selectedAnimal.tagNo} className="w-full h-full object-cover" />
                  ) : !isEditingAnimal && selectedAnimal.photoUrl ? (
                    <img src={selectedAnimal.photoUrl} alt={selectedAnimal.tagNo} className="w-full h-full object-cover" />
                  ) : (
                    <div className="flex flex-col items-center">
                      <span className="text-8xl opacity-30 drop-shadow-2xl">🐄</span>
                      <span className="text-xs text-navy-500 mt-4 font-medium uppercase tracking-wider">Fotoğraf Yüklenmemiş</span>
                    </div>
                  )}

                  {/* Yükle/Değiştir Overlay (Sadece Düzenleme Modunda) */}
                  {isEditingAnimal && (
                    <>
                      <div className="absolute inset-0 bg-navy-950/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center backdrop-blur-sm z-10">
                        <ImageIcon className="w-8 h-8 text-white mb-2" />
                        <span className="text-white text-sm font-medium bg-navy-800/80 px-4 py-2 rounded-lg border border-navy-600">Fotoğrafı Değiştir / Yükle</span>
                      </div>
                      <input
                        type="file"
                        accept="image/jpeg, image/png, image/webp"
                        onChange={handleEditPhotoChange}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-20"
                      />
                    </>
                  )}
                </div>

                {/* Sağ: Bilgiler */}
                <div className="md:col-span-7 space-y-6">

                  {/* Özet Blokları & Edit Modu */}
                  <div className="grid grid-cols-2 gap-3">
                    {/* Branch Edit - Sadece SuperAdmin */}
                    {isSuperAdmin && isEditingAnimal && (
                      <div className="col-span-2 bg-navy-800/50 p-3 rounded-lg border border-navy-700/50 mb-1">
                        <p className="text-xs text-navy-400 mb-2">Atanan Şube / Alt Şube</p>
                        <div className="space-y-2">
                           <select
                             value={(() => {
                               const b = systemBranches.find(br => br.id === editFormData.branch);
                               return b?.parentId || b?.id || '';
                             })()}
                             onChange={(e) => setEditFormData({ ...editFormData, branch: e.target.value })}
                             className="w-full bg-navy-900 border border-navy-600 rounded px-2 py-1.5 text-sm text-white focus:border-forest-500 outline-none"
                           >
                             <option value="">Şube Seçiniz...</option>
                             {systemBranches.filter(b => !b.parentId).map(b => (
                               <option key={b.id} value={b.id}>{b.name}</option>
                             ))}
                           </select>
                           
                           {(() => {
                              const b = systemBranches.find(br => br.id === editFormData.branch);
                              const activeParentId = b?.parentId || b?.id || '';
                              const subBranchesList = systemBranches.filter(sub => sub.parentId === activeParentId);
                              if (activeParentId && subBranchesList.length > 0) {
                                return (
                                  <select
                                    value={editFormData.branch}
                                    onChange={(e) => setEditFormData({ ...editFormData, branch: e.target.value })}
                                    className="w-full bg-navy-900/80 border border-gold-600/30 rounded px-2 py-1.5 text-sm text-gold-400 focus:border-gold-500 outline-none"
                                  >
                                    <option value={activeParentId}>Sadece Merkez ({systemBranches.find(br => br.id === activeParentId)?.name})</option>
                                    {subBranchesList.map(sub => (
                                      <option key={sub.id} value={sub.id}>↳ {sub.name} (Alt Şube)</option>
                                    ))}
                                  </select>
                                );
                              }
                              return null;
                           })()}
                        </div>
                      </div>
                    )}
                    <div className="bg-navy-800/50 p-3 rounded-lg border border-navy-700/50">
                      <p className="text-xs text-navy-400 mb-1">Hayvan No</p>
                      {isEditingAnimal ? (
                        <input type="text" value={editFormData.animalNo} onChange={e => setEditFormData({ ...editFormData, animalNo: e.target.value })} className="w-full bg-navy-900 border border-navy-600 rounded px-2 py-1 text-sm text-white focus:border-forest-500 outline-none" />
                      ) : (
                        <p className="text-base font-bold text-white">{selectedAnimal.animalNo || '-'}</p>
                      )}
                    </div>
                    <div className="bg-navy-800/50 p-3 rounded-lg border border-navy-700/50">
                      <p className="text-xs text-navy-400 mb-1">Küpe No</p>
                      {isEditingAnimal ? (
                        <input type="text" value={editFormData.tagNo} onChange={e => setEditFormData({ ...editFormData, tagNo: e.target.value })} className="w-full bg-navy-900 border border-navy-600 rounded px-2 py-1 text-sm text-white focus:border-forest-500 outline-none" />
                      ) : (
                        <p className="text-base font-bold text-white">{selectedAnimal.tagNo}</p>
                      )}
                    </div>
                    <div className="bg-forest-900/20 p-3 rounded-lg border border-forest-500/20">
                      <p className="text-xs text-forest-400/80 mb-1">Kesim Sırası</p>
                      {isEditingAnimal ? (
                        <input type="text" value={editFormData.slaughterNo} onChange={e => setEditFormData({ ...editFormData, slaughterNo: e.target.value })} disabled={userProfile?.role === 'subAdmin'} className="w-full bg-navy-900 border border-forest-600/50 rounded px-2 py-1 text-sm text-forest-400 focus:border-forest-400 outline-none disabled:opacity-50" />
                      ) : (
                        <p className="text-base font-bold text-forest-400">{selectedAnimal.slaughterNo}</p>
                      )}
                    </div>
                    <div className="bg-navy-800/50 p-3 rounded-lg border border-navy-700/50 col-span-2 sm:col-span-1">
                      <p className="text-xs text-navy-400 mb-1">Ağırlık Aralığı (Yaklaşık Olarak)</p>
                      {isEditingAnimal ? (
                        <div className="flex items-center gap-1">
                          <input type="number" value={editFormData.weightMin} onChange={e => setEditFormData({ ...editFormData, weightMin: e.target.value })} className="w-full bg-navy-900 border border-navy-600 rounded px-2 py-1 text-sm text-white focus:border-forest-500 outline-none text-center" placeholder="Min" />
                          <span className="text-navy-400">-</span>
                          <input type="number" value={editFormData.weightMax} onChange={e => setEditFormData({ ...editFormData, weightMax: e.target.value })} className="w-full bg-navy-900 border border-navy-600 rounded px-2 py-1 text-sm text-white focus:border-forest-500 outline-none text-center" placeholder="Max" />
                          <span className="text-xs text-navy-400">KG</span>
                        </div>
                      ) : (
                        <p className="text-base font-bold text-white">{selectedAnimal.weight} <span className="text-xs text-navy-400 font-normal">KG</span></p>
                      )}
                    </div>
                    <div className="bg-gold-500/10 p-3 rounded-lg border border-gold-500/20">
                      <p className="text-xs text-gold-400/80 mb-1">{selectedAnimal.type === 'buyukbas' ? 'Toplam Fiyat' : 'Fiyat'}</p>
                      {isEditingAnimal ? (
                        <div className="flex items-center gap-1"><input type="number" value={editFormData.price} onChange={e => setEditFormData({ ...editFormData, price: e.target.value })} className="w-full bg-navy-900 border border-gold-600/50 rounded px-2 py-1 text-sm text-gold-400 focus:border-gold-400 outline-none" /></div>
                      ) : (
                        <p className="text-base font-bold text-gold-400">{selectedAnimal.price.toLocaleString('tr-TR')} <span className="text-xs font-normal">₺</span></p>
                      )}
                    </div>
                    {selectedAnimal.type === 'buyukbas' && (
                      <div className="bg-navy-800/50 p-3 rounded-lg border border-navy-700/50">
                        <p className="text-xs text-navy-400 mb-1">Toplam Hisse</p>
                        {isEditingAnimal ? (
                          <input type="number" max="7" min={selectedAnimal.shareCount || 1} value={editFormData.totalShares || selectedAnimal.totalShares} onChange={e => setEditFormData({ ...editFormData, totalShares: e.target.value })} className="w-full bg-navy-900 border border-navy-600 rounded px-2 py-1 text-sm text-white focus:border-forest-500 outline-none" placeholder="Örn: 7" />
                        ) : (
                          <p className="text-base font-bold text-white">{selectedAnimal.totalShares}</p>
                        )}
                      </div>
                    )}
                  </div>

                  {isEditingAnimal && (
                    <button
                      onClick={handleUpdateSubmit}
                      disabled={isUpdating}
                      className="w-full py-2 bg-gradient-to-r from-forest-600 to-forest-700 hover:from-forest-500 hover:to-forest-600 rounded-lg text-white font-bold flex items-center justify-center gap-2 transition-all disabled:opacity-50"
                    >
                      {isUpdating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      Kaydet
                    </button>
                  )}

                  {/* Hisse Durumu */}
                  <div className="pt-4 border-t border-navy-700/50">
                    <div className="flex justify-between items-end mb-2">
                      <div>
                        <p className="text-sm font-semibold text-white">Hisse Durumu</p>
                        <p className="text-xs text-navy-400 flex items-center gap-1 mt-0.5">
                          Tür: <span className="text-white capitalize">{selectedAnimal.type === 'buyukbas' ? 'Büyükbaş (7)' : 'Küçükbaş (1)'}</span>
                        </p>
                      </div>
                      <div className="text-right">
                        <span className={`text-xl font-bold ${selectedAnimal.shareCount === selectedAnimal.totalShares ? 'text-danger-400' : 'text-forest-400'}`}>
                          {selectedAnimal.shareCount}
                        </span>
                        <span className="text-sm text-navy-400"> / {selectedAnimal.totalShares}</span>
                      </div>
                    </div>

                    <div className="h-2 w-full bg-navy-800 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${selectedAnimal.shareCount === selectedAnimal.totalShares ? 'bg-danger-500' : 'bg-forest-500'} transition-all`}
                        style={{ width: `${(selectedAnimal.shareCount / selectedAnimal.totalShares) * 100}%` }}
                      />
                    </div>
                  </div>

                </div>
              </div>

              {/* Hissedar Listesi (TAM GENİŞLİK - KARTIN ALTINA YAYILACAK) */}
              <div className="pt-6 border-t border-navy-700/50">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <p className="text-sm font-semibold text-white">Bağlı Hissedarlar</p>
                    <span className="text-[10px] text-navy-400 font-bold bg-navy-800 px-2 py-0.5 rounded">{animalShares.length} Kişi</span>
                  </div>

                  {selectedAnimal.shareCount < selectedAnimal.totalShares && (
                    <button
                      onClick={() => {
                        setSelectedAnimal(null);
                        navigate(`/dashboard/hissedarlar?preSelectAnimal=${selectedAnimal.id}`);
                      }}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-forest-600 hover:bg-forest-500 text-white text-xs font-bold transition-all shadow-md shadow-forest-500/20"
                    >
                      <PlusCircle className="w-3.5 h-3.5" />
                      Hissedar Ekle
                    </button>
                  )}
                </div>
                {loadingShares ? (
                  <p className="text-xs text-navy-400">Yükleniyor...</p>
                ) : animalShares.length === 0 ? (
                  <p className="text-xs text-navy-400 italic">Bu kurbana henüz hissedar atanmamış.</p>
                ) : (
                  <div className="space-y-3 max-h-64 overflow-y-auto pr-1 custom-scrollbar">
                    {animalShares.map(share => {
                      const percent = Math.min(((share.paidAmount || 0) / (share.price || 1)) * 100, 100);

                      const handleProxyWhatsApp = (e, s) => {
                        e.stopPropagation();
                        const message = `Sayın ${s.customerFirstName} ${s.customerLastName}, Hasımoğlu Çiftliği kurban kesim işlemlerinizin İslami usullere uygun olarak gerçekleştirilebilmesi için vekaletinize ihtiyacımız vardır. Lütfen bu mesaja "Vekalet Veriyorum" şeklinde cevap veriniz.`;
                        const phone = s.customerPhone?.replace(/[^0-9]/g, '') || '';
                        let waPhone = phone;
                        if (waPhone.startsWith('0')) waPhone = waPhone.substring(1);
                        if (!waPhone.startsWith('90') && waPhone.length === 10) waPhone = '90' + waPhone;
                        window.open(`https://wa.me/${waPhone}?text=${encodeURIComponent(message)}`, '_blank');
                      };

                      return (
                        <div
                          key={share.id}
                          onClick={() => {
                            setSelectedAnimal(null);
                            navigate(`/dashboard/hissedarlar?highlight=${share.id}&openPayment=true`);
                          }}
                          className="relative overflow-hidden p-4 rounded-xl bg-navy-800/40 border border-navy-700/50 hover:border-forest-500/50 cursor-pointer transition-all group"
                        >
                          {/* Ödeme Barı (Background Fill) */}
                          <div
                            className={`absolute left-0 top-0 bottom-0 transition-all duration-1000 ease-out z-0 ${percent >= 100 ? 'bg-forest-900/40' : 'bg-gold-900/20'}`}
                            style={{ width: `${percent}%` }}
                          />

                          {/* Kart İçeriği */}
                          <div className="relative z-10 flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xs text-white font-bold shadow-inner ${percent >= 100 ? 'bg-forest-600' : 'bg-navy-700 group-hover:bg-navy-600'}`}>
                                {share.customerFirstName?.[0]?.toUpperCase()}{share.customerLastName?.[0]?.toUpperCase()}
                              </div>
                              <div className="flex flex-col">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-lg text-white font-bold tracking-wide leading-tight">{share.customerFirstName} {share.customerLastName}</span>
                                  {share.isProxyGiven ? (
                                    <span className="flex items-center gap-1 text-[10px] bg-forest-900/40 text-forest-400 border border-forest-500/30 px-1.5 py-0.5 rounded cursor-default" title="Vekalet Alındı">
                                      <UserCheck className="w-3 h-3" /> VEKALET TAMAM
                                    </span>
                                  ) : (
                                    <button onClick={(e) => handleProxyWhatsApp(e, share)} className="flex items-center gap-1.5 text-[11px] font-bold bg-red-600 border border-red-500 text-white hover:bg-red-700 hover:shadow-[0_0_15px_rgba(220,38,38,0.6)] px-2 py-0.5 rounded transition-all shadow-lg animate-pulse hover:animate-none" title="Vekalet Eksik! WhatsApp'tan İste">
                                      <MessageCircle className="w-3.5 h-3.5" /> VEKALET İSTE
                                    </button>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-[11px] text-navy-400 font-mono tracking-widest">{share.customerPhone}</span>
                                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-navy-950/60 text-gold-400 border border-gold-500/20 shadow-sm">
                                    ÖDENEN: {(share.paidAmount || 0).toLocaleString('tr-TR')} ₺ / {share.price.toLocaleString('tr-TR')} ₺
                                  </span>
                                  {percent >= 100 && (
                                    <span className="text-[10px] font-bold text-forest-400 border border-forest-500/30 px-2 py-0.5 rounded-full bg-forest-900/30 shadow-sm">
                                      BORÇ BİTTİ
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <span className="text-xs uppercase font-bold text-navy-400 group-hover:text-forest-400 transition-colors bg-navy-900 px-4 py-2.5 rounded-lg border border-navy-700 shadow-sm">
                              Tahsilat Modülüne Gir →
                            </span>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>

            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-navy-700/50 bg-navy-900/50 flex justify-end">
              <button
                onClick={() => setSelectedAnimal(null)}
                className="px-5 py-2 bg-navy-800 hover:bg-navy-700 text-white rounded-lg transition-colors text-sm font-medium"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── ŞİFRELİ SİLME ONAY MODALI (KURBAN) ── */}
      {deletePromptOpen && selectedAnimal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={() => setDeletePromptOpen(false)} />
          <div className="relative w-full max-w-sm glass border border-danger-500/50 rounded-2xl overflow-hidden shadow-2xl p-6 bg-navy-900 animate-in zoom-in-95">
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-full bg-danger-500/20 flex items-center justify-center mb-4 border border-danger-500/50">
                <AlertCircle className="w-6 h-6 text-danger-500" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">Kurban Kaydını Sil</h3>
              <p className="text-xs text-navy-300 mb-6">
                <span className="font-bold text-white uppercase">{selectedAnimal.tagNo}</span> küpeli kurbanı siliyorsunuz. İçindeki tüm hissedarlar zarar görmeden <b>(Boşta/Atanmamış)</b> durumuna geçecektir. Lütfen eylemi onaylamak için Yönetici Şifrenizi girin.
              </p>

              {deleteError && (
                <div className="w-full bg-danger-500/10 border border-danger-500/20 text-danger-500 text-xs py-2 rounded mb-4">
                  {deleteError}
                </div>
              )}

              <input
                type="password"
                placeholder="Şifreyi giriniz..."
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                className="w-full px-4 py-3 rounded-lg bg-navy-950 border border-navy-700 text-white focus:border-danger-500 outline-none mb-6 text-center text-lg tracking-widest"
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && executeDeleteAnimal()}
              />

              <div className="flex gap-3 w-full">
                <button
                  onClick={() => setDeletePromptOpen(false)}
                  className="flex-1 py-2.5 rounded-lg bg-navy-800 text-navy-300 hover:text-white transition-colors text-sm font-semibold"
                >
                  İptal
                </button>
                <button
                  onClick={executeDeleteAnimal}
                  className="flex-1 py-2.5 rounded-lg bg-danger-600 hover:bg-danger-500 text-white transition-colors text-sm font-bold shadow-lg shadow-danger-500/20"
                >
                  Kalıcı Olarak Sil
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
